// The tournament's referee roster for admins and referee assigners: who was
// invited on which terms, how they answered, and the invite and revoke actions

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Avatar from './Avatar';
import Spinner from './Spinner';
import RefereeInviteModalBody from './RefereeInviteModalBody';
import { useFirebase } from './FirebaseContextProvider';
import { useModal } from './ModalProvider';
import { useEntities, useTournamentParts } from '../utils/hooks';
import { RefereeStatuses } from '../utils/enums';
import { tournamentRefereeRevoke } from '../utils/firebase';
import { moneyStr } from '../utils/gameReport';
import './RefereeRoster.scss';

const noRoster = [];

const statusOrder = [
  RefereeStatuses.ACCEPTED,
  RefereeStatuses.INVITED,
  RefereeStatuses.DECLINED,
  RefereeStatuses.REVOKED,
];

const statusLabels = {
  [RefereeStatuses.ACCEPTED]: 'Accepted',
  [RefereeStatuses.INVITED]: 'Invited',
  [RefereeStatuses.DECLINED]: 'Declined',
  [RefereeStatuses.REVOKED]: 'Revoked',
};

// An open offer carries its fee. Accepting moves it into the referee's rate,
// which the roster document no longer holds; undefined is shown as a dash
const payOf = (row, getRates) => {
  const rate = getRates(row.id)?.rate;
  if (row.status === RefereeStatuses.ACCEPTED) return rate;
  if (row.status === RefereeStatuses.REVOKED) return rate ?? row.fee;
  return row.fee;
};

export default function RefereeRoster({ id, getRates }) {
  const [spinner, setSpinner] = useState(false);
  const { tournamentsById } = useFirebase();
  const { openModal } = useModal();

  useTournamentParts(id, ['referees']);
  const tournament = tournamentsById.get(id);
  const roster = tournament?.referees ?? noRoster;
  const isLoaded = tournament?.refereesLoaded === true;
  const isSynced = tournament?.refereesSynced === true;

  const { entities } = useEntities(
    'users',
    roster.map((entry) => entry.id),
  );
  const nameById = useMemo(
    () => new Map(entities.filter(Boolean).map((u) => [u.id, u])),
    [entities],
  );

  const rows = useMemo(
    () =>
      roster
        .map((entry) => ({
          ...entry,
          user: nameById.get(entry.id),
          pay: payOf(entry, getRates),
        }))
        .sort(
          (a, b) =>
            statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status) ||
            (a.user?.name ?? '').localeCompare(b.user?.name ?? '', 'en', {
              sensitivity: 'base',
            }),
        ),
    [roster, nameById, getRates],
  );

  const invite = () =>
    openModal({
      header: 'Invite referees',
      body: <RefereeInviteModalBody tournamentId={id} roster={roster} />,
    });

  const revoke = (row) => {
    const handleRevoke = async () => {
      setSpinner(true);
      try {
        await tournamentRefereeRevoke({ id, refereeId: row.id });
      } catch (error) {
        toast.error(error?.message ?? 'Failed to revoke the referee');
      } finally {
        setSpinner(false);
      }
    };

    openModal({
      header: 'Revoke referee',
      body: (
        <p className='font-16'>
          Take <span className='font-medium'>{row.user?.name}</span> off the
          referee roster? Games they have worked stay paid at their rate.
        </p>
      ),
      buttons: [
        { text: 'Cancel', className: 'bw' },
        { text: 'Revoke', className: 'danger', onClick: handleRevoke },
      ],
    });
  };

  return (
    <div className='referee-roster'>
      <div className='controls'>
        <h5>Referee roster</h5>
        {/* The picker leaves out the accepted, so it needs the roster first */}
        <button className='small' onClick={invite} disabled={!isLoaded}>
          Invite referees
        </button>
      </div>
      {!isLoaded && <Spinner show inline />}
      {isSynced && rows.length === 0 && (
        <p className='empty'>No referees invited yet</p>
      )}
      <ul className='rows'>
        {rows.map((row) => {
          const isOpen =
            row.status === RefereeStatuses.INVITED ||
            row.status === RefereeStatuses.ACCEPTED;

          return (
            <li key={row.id} className={`row ${row.status}`}>
              <Link to={`/referees/${row.id}`} className='who'>
                <Avatar user={row.user} size={32} />
                <p className='font-medium'>{row.user?.name ?? '—'}</p>
              </Link>
              <p className='status'>{statusLabels[row.status]}</p>
              <p className='fee'>
                {row.pay === undefined ? '—' : `${moneyStr(row.pay)} / game`}
              </p>
              <p className='requirements'>{row.requirements}</p>
              {isOpen && (
                <button
                  className='small bw'
                  onClick={() => revoke(row)}
                  disabled={spinner}
                >
                  Revoke
                </button>
              )}
            </li>
          );
        })}
      </ul>
      <Spinner show={spinner} />
    </div>
  );
}
